/*
 * The Mako page's shortcuts section (sections/mako-keys.liquid). First a
 * shortcut is set the way the plugin does it: right-click a button,
 * Keyboard Shortcut, press the keys, Save. Then every button's shortcut is
 * pressed in turn: the keys show big under the window, the button lights
 * up with its tooltip, and what it does lands on the timeline. Then the
 * timeline clears and the shortcuts play again.
 *
 * Plays only while on screen; leaving stops it and it starts over next
 * time. With reduced motion it shows the finished timeline and one
 * shortcut instead.
 */
(() => {
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  const STOP = {};
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  // Shortcuts are written the Mac way (⌥+1); on other systems the
  // modifiers read as Windows names them.
  const platform = (navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || navigator.userAgent;
  const isMac = /mac|iphone|ipad|ipod/i.test(platform);
  const WIN = { '⌘': 'Ctrl', '⌃': 'Ctrl', '⌥': 'Alt', '⇧': 'Shift' };
  const keyName = (k) => (isMac ? k : WIN[k] || k);
  const caps = (keys) => String(keys).split('+').map((k) => keyName(k.trim())).filter(Boolean);
  // How a shortcut reads in a menu or field: ⌥1 on a Mac, Alt+1 elsewhere.
  const label = (keys) => caps(keys).join(isMac ? '' : '+');
  // Running text: "like ⌥1" becomes "like Alt+1".
  const forOs = (text) =>
    isMac ? text : text.replace(/([⌃⌥⇧⌘]+)(\S)/g, (m, mods, key) => [...mods].map((c) => WIN[c]).concat(key).join('+'));

  const init = (root) => {
    if (root.dataset.kkReady) return;
    root.dataset.kkReady = 'true';

    const q = (sel) => root.querySelector(sel);
    const stage = q('[data-kk-stage]');
    const tools = [...root.querySelectorAll('[data-kk-tool]')];
    if (!stage || !tools.length) return;
    const hud = q('[data-kk-hud]');
    const capsEl = q('[data-kk-caps]');
    const hudName = q('[data-kk-hudname]');
    const tip = q('[data-kk-tip]');
    const menu = q('[data-kk-menu]');
    const dialog = q('[data-kk-dialog]');
    const field = q('[data-kk-field]');
    const cursor = q('[data-kk-cursor]');
    const tab1 = q('[data-kk-tab="1"]');
    const tab2 = q('[data-kk-tab="2"]');

    root.querySelectorAll('[data-kk-os]').forEach((el) => (el.textContent = forOs(el.textContent)));

    let run = 0;
    let playing = false;
    let setUp = false;

    const wait = (r, ms) =>
      new Promise((resolve, reject) => {
        window.setTimeout(() => (r === run ? resolve() : reject(STOP)), ms);
      });

    // Where an element sits inside the stage, in px.
    const spot = (el, fx = 0.5, fy = 0.5) => {
      const s = stage.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      return [r.left - s.left + r.width * fx, r.top - s.top + r.height * fy];
    };
    const place = (el, x, y) => {
      el.style.setProperty('--tx', `${x}px`);
      el.style.setProperty('--ty', `${y}px`);
    };
    const moveTo = (r, el, fx, fy, ms = 650) => {
      const [x, y] = spot(el, fx, fy);
      cursor.style.setProperty('--cx', `${x}px`);
      cursor.style.setProperty('--cy', `${y}px`);
      return wait(r, ms);
    };
    const tap = (r) => {
      cursor.classList.remove('is-click');
      void cursor.offsetWidth;
      cursor.classList.add('is-click');
      return wait(r, 200);
    };

    const showKeys = (keys, name) => {
      capsEl.innerHTML = caps(keys)
        .map((k) => `<span class="kk-cap">${esc(k)}</span>`)
        .join('<span class="kk-plus">+</span>');
      hudName.textContent = name;
      hud.classList.add('is-on');
    };
    const pressKeys = async (r) => {
      const all = [...capsEl.querySelectorAll('.kk-cap')];
      // modifiers go down first, the key last, like fingers do
      for (const cap of all) {
        cap.classList.add('is-down');
        await wait(r, 110);
      }
      await wait(r, 180);
      all.forEach((cap) => cap.classList.remove('is-down'));
    };

    const apply = (tool) => {
      root.querySelectorAll(`[data-on~="${tool}"]`).forEach((el) => el.classList.add('is-on'));
      root.querySelectorAll(`[data-off~="${tool}"]`).forEach((el) => el.classList.add('is-off'));
      if (tool === 'sequence' && tab2) {
        tab1.classList.remove('is-current');
        tab2.classList.add('is-current');
        stage.classList.add('is-seq2');
      }
    };

    // back to the sequence being edited
    const showSequence1 = () => {
      tab1.classList.add('is-current');
      if (tab2) tab2.classList.remove('is-current');
      stage.classList.remove('is-seq2');
    };

    const clear = () => {
      root.querySelectorAll('[data-on], [data-off]').forEach((el) => el.classList.remove('is-on', 'is-off'));
      showSequence1();
      tools.forEach((t) => t.classList.remove('is-pressed', 'is-menu'));
      tip.classList.remove('is-on');
      menu.classList.remove('is-open');
      dialog.classList.remove('is-open', 'is-ready');
      field.classList.remove('is-set');
      field.textContent = field.dataset.placeholder || '';
      cursor.classList.remove('is-on');
      hud.classList.remove('is-on');
    };

    const showTip = (button) => {
      q('[data-kk-tipname]').textContent = button.dataset.name;
      q('[data-kk-tipkeys]').textContent = label(button.dataset.keys);
      const vertical = getComputedStyle(stage).gridTemplateColumns.split(' ').length > 1;
      const [x, y] = vertical ? spot(button, 1, 0.5) : spot(button, 0.5, 1);
      place(tip, vertical ? x + 8 : Math.max(8, x - 60), vertical ? y - 12 : y + 8);
      tip.classList.add('is-on');
    };

    // Setting the first shortcut, as in the plugin.
    const setShortcut = async (r) => {
      const button = tools[0];
      cursor.classList.add('is-on');
      await wait(r, 300);
      await moveTo(r, button);
      await tap(r);
      button.classList.add('is-menu');
      const [x, y] = spot(button, 0.6, 0.6);
      place(menu, x, y);
      menu.classList.add('is-open');
      await wait(r, 420);
      const item = q('[data-kk-menuitem]');
      await moveTo(r, item, 0.4, 0.5, 450);
      item.classList.add('is-hot');
      await tap(r);
      menu.classList.remove('is-open');
      item.classList.remove('is-hot');
      button.classList.remove('is-menu');
      q('[data-kk-dialogtitle]').textContent = button.dataset.name;
      dialog.classList.add('is-open');
      await wait(r, 700);
      showKeys(button.dataset.keys, button.dataset.name);
      await pressKeys(r);
      field.textContent = label(button.dataset.keys);
      field.classList.add('is-set');
      dialog.classList.add('is-ready');
      await wait(r, 500);
      await moveTo(r, dialog.querySelector('.kk-save'), 0.5, 0.5);
      await tap(r);
      dialog.classList.remove('is-open', 'is-ready');
      cursor.classList.remove('is-on');
      hud.classList.remove('is-on');
      await wait(r, 700);
    };

    const play = async () => {
      run += 1;
      const r = run;
      playing = true;
      clear();
      try {
        if (!setUp) {
          await setShortcut(r);
          setUp = true;
        }
        for (;;) {
          for (const button of tools) {
            showKeys(button.dataset.keys, button.dataset.name);
            await wait(r, 450);
            await pressKeys(r);
            button.classList.add('is-pressed');
            showTip(button);
            apply(button.dataset.kkTool);
            await wait(r, 260);
            button.classList.remove('is-pressed');
            if (button.dataset.kkTool === 'sequence') {
              await wait(r, 1000);
              showSequence1();
              await wait(r, 400);
            } else await wait(r, 1400);
            tip.classList.remove('is-on');
          }
          await wait(r, 1200);
          hud.classList.remove('is-on');
          stage.style.opacity = '0.35';
          await wait(r, 380);
          clear();
          stage.style.opacity = '';
          await wait(r, 900);
        }
      } catch (error) {
        if (error !== STOP) throw error;
      }
    };

    const stop = () => {
      run += 1;
      playing = false;
      stage.style.opacity = '';
      clear();
    };

    const finished = () => {
      tools.forEach((button) => apply(button.dataset.kkTool));
      showSequence1();
      showKeys(tools[0].dataset.keys, tools[0].dataset.name);
    };

    if (still.matches || !('IntersectionObserver' in window)) {
      finished();
      return;
    }

    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!playing) play();
          } else if (playing) {
            stop();
          }
        });
      },
      { threshold: 0.4 }
    ).observe(stage);
  };

  const boot = () => document.querySelectorAll('[data-mako-keys]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
